import { Injectable } from '@angular/core';
import { SEEN_NO_ADDRESS_MODAL, SEEN_ONBOARDING } from '@app/core/constants/app.constants';
import { SentryService } from '@app/core/services/internal/sentry.service';
import { AnalyticsService } from '@app/core/services/internal/analytics.service';
import { UserService } from '@app/core/services/global/user.service';
import { ClientHomeStateService } from '@app/core/services/client/client-home-state.service';
import { ClientStudiesStateService } from '@app/core/services/client/client-studies-state.service';
import { ClientHistoryStateService } from '@app/core/services/client/client-history-state.service';
import { StorageLoader } from '@app/utils/storageLoader';
import { NavController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';

interface AuthResponse {
  access_token: string;
  refresh_token: string;
  user: any;
  system_info?: any;
  system_menu?: any;
  data?: AuthResponse;
}

@Injectable({
  providedIn: 'root',
})
export class AuthSessionService {
  private authStateSubject = new BehaviorSubject<boolean>(false);
  private refreshPromise: Promise<boolean> | null = null;

  authState = this.authStateSubject.asObservable();

  constructor(
    private navCtrl: NavController,
    private userService: UserService,
    private storageLoader: StorageLoader,
    private sentry: SentryService,
    private analytics: AnalyticsService,
    private clientHomeState: ClientHomeStateService,
    private clientStudiesState: ClientStudiesStateService,
    private clientHistoryState: ClientHistoryStateService,
  ) {}

  get isLoggedIn(): boolean {
    return this.authStateSubject.value;
  }

  async ifLoggedIn(isAuthenticated: boolean = false): Promise<boolean> {
    if (isAuthenticated || this.isLoggedIn) {
      return true;
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.refreshSession();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  async login(data: AuthResponse, redirect: boolean = true): Promise<void> {
    const authData = this.normalizeAuthData(data);
    await this.persistAuthData(authData);

    if (authData.user) {
      this.sentry.identify({
        id: authData.user.id,
        email: authData.user.email,
      });
      this.analytics.identify(String(authData.user.id), {
        email: authData.user.email,
        religion: authData.user.religion,
        environment: authData.user.environment,
      });
    }

    this.authStateSubject.next(true);

    if (redirect) {
      await this.redirect();
    }
  }

  async logout(): Promise<void> {
    await this.clearSession();

    await this.navCtrl.navigateRoot(['/']);
  }

  async isAuthenticated(): Promise<boolean> {
    return await this.ifLoggedIn(this.isLoggedIn);
  }

  private async refreshSession(): Promise<boolean> {
    const user = await this.storageLoader.getUser();

    if (!user) {
      this.authStateSubject.next(false);
      return false;
    }

    try {
      const response = await this.userService.refresh();
      const authData = this.normalizeAuthData(response);

      if (!authData?.access_token) {
        await this.clearSession();
        return false;
      }

      await this.persistAuthData(authData);

      this.authStateSubject.next(true);

      return true;
    } catch (error) {
      console.error('[Error] Refresh auth session:', error);

      await this.clearSession();

      return false;
    } finally {
    }
  }

  private async persistAuthData(data: AuthResponse): Promise<void> {
    await this.storageLoader.setToken(data.access_token);
    await this.storageLoader.setRefreshToken(data.refresh_token);

    if (data.user) {
      await this.storageLoader.setUser(data.user);
    }

    if (data.system_info) {
      await this.storageLoader.setSystemInfo(data.system_info);
    }

    if (data.system_menu) {
      await this.storageLoader.setSystemMenu(data.system_menu);
    }
  }

  private normalizeAuthData(data: AuthResponse): AuthResponse {
    return data?.data ?? data;
  }

  private async clearSession(): Promise<void> {
    const menuMode = await this.storageLoader.getIsModeMenu();

    await this.storageLoader.clear();

    if (menuMode) {
      await this.storageLoader.changeModeMenu();
    }

    
    
    try {
      localStorage.removeItem(SEEN_NO_ADDRESS_MODAL);
      sessionStorage.removeItem(SEEN_NO_ADDRESS_MODAL);
      sessionStorage.removeItem(SEEN_ONBOARDING);
    } catch {

    }

    this.sentry.clearUser();
    this.analytics.reset();
    this.clientHomeState.reset();
    this.clientStudiesState.reset();
    this.clientHistoryState.reset();

    this.authStateSubject.next(false);
  }

  private async redirect(): Promise<void> {
    
    
    
    if (await this.shouldShowOnboarding()) {
      await this.navCtrl.navigateRoot(['/onboarding']);
      return;
    }
    await this.navCtrl.navigateRoot(['/dashboard']);
  }

  
  private async shouldShowOnboarding(): Promise<boolean> {
    try {
      const user = await this.storageLoader.getUser();
      
      if (!user || user?.role === 'admin' || user?.role === 'system') {
        return false;
      }
      if (user.onboarding_completed === true) {
        return false;
      }
      try {
        if (sessionStorage.getItem(SEEN_ONBOARDING) === '1') {
          return false;
        }
      } catch {
        
        
      }
      return true;
    } catch {
      return false;
    }
  }
}
